// RunProgressStripV2 — slim, horizontal "what is the agent doing right
// now" strip. Designed for tight surfaces (AiChatFloatingV2's panel,
// embeds, etc.) where the full RunProgressPanelV2 sidebar is too heavy.
//
// Layout (single row, ~28px tall):
//   ┌─────────────────────────────────────────────────────────────┐
//   │ ●  current task / tool / "em execução…"      [3/7]  [🧠 5] │
//   └─────────────────────────────────────────────────────────────┘
//
//   • Connection dot — small pulsing circle, color-coded by
//     ConnectionState (Connected/Connecting/Disconnected/Idle). Same
//     palette as the full panel's header dot, just smaller.
//   • Activity text — the current task label (from the agent's
//     todoList.currentWorkingItemKey) when there's one in flight,
//     otherwise the last tool name, otherwise a generic "em execução…"
//     while a turn is open. Truncates with ellipsis on small panels.
//   • Task progress chip — completed/total, only when a todoList exists.
//   • Tool count chip — count of non-silent function calls in the
//     current turn, with the brain glyph the full panel uses on its
//     collapsed rail. Subscribed in a leaf so per-WS-partial churn
//     on `currentMessage`/`messages` doesn't re-render the strip.
//
// Visibility: the strip mounts only when there's something *worth*
// showing. When `turnOpen` is false AND no todoList AND no recent tool
// event AND connection is Idle/Disconnected, it returns `null` so the
// host surface (e.g. AiChatFloatingV2) collapses to its normal height.
// This keeps the panel from gaining a permanent "1px of chrome" line
// when the user isn't running anything.

import React, { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useAiChatSessionStore } from '../AiChat/AiChatContext';
import { ConnectionState } from '../../models/connectionState';
import type { TodoList } from '../../models/todoListModel';
import type { Interaction } from '../../models/Interaction';
import type { InteractionPart } from '../../models/InteractionPart';
import { getFriendlyLabel, type FriendlyState } from '../AiChat/ChatPane/friendlyToolConfig';
import { BrainIcon } from './icons';

// Same parser the full RunProgressPanelV2 uses to extract the agent's
// todoList from the latest tool event. Kept as a local copy so the
// strip stays self-contained — if you change one, change both.
function tryParseMainAgentTodoList(
    eventName: string | undefined,
    eventData: string | undefined,
    subAgentId: string | undefined,
): TodoList | null {
    if (!eventName || eventName !== 'todolistUpdated') return null;
    if (subAgentId) return null;
    if (!eventData) return null;
    try {
        const parsed = JSON.parse(eventData) as TodoList;
        if (!parsed || !Array.isArray(parsed.items)) return null;
        return parsed;
    } catch {
        return null;
    }
}

const pulseKeyframes = `@keyframes v2rpspulse{0%,100%{opacity:1}50%{opacity:.35}}`;

/**
 * Leaf for the tool-call counter chip. Subscribes only here so the
 * per-partial churn on `currentMessage`/`messages` doesn't re-render
 * the rest of the strip.
 */
const ToolCountChip: React.FC = React.memo(() => {
    const count = useAiChatSessionStore((s) => {
        const interaction = s.currentMessage ?? s.messages?.[s.messages.length - 1] ?? null;
        if (!interaction?.parts) return 0;
        return interaction.parts.filter(
            (p: InteractionPart) =>
                (p.eventType === 'function_call' || p.eventType === 'partial_function_call') &&
                !p.functionDetails?.silent,
        ).length;
    });
    if (count <= 0) return null;
    return (
        <Tooltip title={`${count} ferramentas`} placement="top">
            <Box
                sx={(theme) => ({
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 0.75,
                    height: 20,
                    borderRadius: 0.75,
                    backgroundColor: theme.palette.surface.raised,
                    border: `1px solid ${theme.palette.divider}`,
                    color: theme.palette.primary.main,
                    flexShrink: 0,
                })}
            >
                <BrainIcon size={11} />
                <Box
                    component="span"
                    sx={(theme) => ({
                        fontFamily: theme.typography.fontFamilyMono,
                        fontSize: 10,
                        color: theme.palette.text.primary,
                        lineHeight: 1,
                    })}
                >
                    {count}
                </Box>
            </Box>
        </Tooltip>
    );
});
ToolCountChip.displayName = 'ToolCountChip';

/**
 * Map an internal tool/event name into a short human-friendly label.
 * The agent emits raw event names like `todolistUpdated`,
 * `executeQuery`, etc. Most are meaningful enough to surface as-is;
 * a few we want to soften (the meta `todolistUpdated` is filtered
 * out upstream because we use the parsed list instead).
 */
function humanizeEventName(name: string): string {
    // Insert spaces before capital letters in camelCase / PascalCase.
    return name
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .toLowerCase();
}

export const RunProgressStripV2: React.FC = () => {
    const theme = useTheme();
    const turnOpen = useAiChatSessionStore((s) => s.turnOpen);
    const connectionStatus = useAiChatSessionStore((s) => s.connectionStatus);
    const lastToolEvent = useAiChatSessionStore((s) => s.lastToolEvent);

    const todoList = useMemo(
        () => tryParseMainAgentTodoList(
            lastToolEvent?.eventName,
            lastToolEvent?.eventData,
            lastToolEvent?.subAgentId,
        ),
        [lastToolEvent?.eventName, lastToolEvent?.eventData, lastToolEvent?.subAgentId],
    );

    // The actual tool calls live on the interaction parts, NOT on
    // `lastToolEvent` — `lastToolEvent` is for agent-emitted side
    // events (`todolistUpdated`, `dashboardGenerated`, ...), written
    // by `AgentGatewayService.localEmitToolEvent` only when a part
    // carries `toolDetails`. Most agents (including the dashboard
    // analysis agent) emit their work through plain
    // `function_call` / `partial_function_call` / `function_response`
    // parts and never touch `lastToolEvent`.
    //
    // Mirror `AgentActivityV2`'s read pattern: walk the latest
    // interaction's parts, take the last non-silent function-event,
    // and feed it through `getFriendlyLabel` so the strip shows the
    // same Portuguese phrasing the activity timeline does
    // ("Consultando série de vendas" instead of "execute query").
    //
    // Note on `function_response`: when a tool call completes the
    // runtime mutates the SAME part's eventType from `function_call`
    // to `function_response` (response merged into functionDetails).
    // We include it in the filter so the strip keeps showing the
    // most-recent tool name during the brief window between calls,
    // instead of flickering back to the generic fallback.
    const currentMessage = useAiChatSessionStore((s) => s.currentMessage);
    const messages = useAiChatSessionStore((s) => s.messages);

    const latestCall = useMemo<{ label: string } | null>(() => {
        const interaction: Interaction | null =
            currentMessage ?? messages?.[messages.length - 1] ?? null;
        if (!interaction?.parts) return null;
        for (let i = interaction.parts.length - 1; i >= 0; i--) {
            const p: InteractionPart = interaction.parts[i];
            const isFnEvent =
                p.eventType === 'function_call'
                || p.eventType === 'partial_function_call'
                || p.eventType === 'function_response';
            if (!isFnEvent) continue;
            const fn = p.functionDetails;
            if (!fn || fn.silent) continue;
            const friendlyState: FriendlyState =
                p.eventType === 'function_response'
                    ? 'response'
                    : p.eventType === 'partial_function_call' || fn.status === 'Composing'
                        ? 'partial'
                        : 'call';
            const isError =false;
                
            const label = getFriendlyLabel(fn.name, friendlyState, fn.args ?? fn.text, isError);
            if (!label) continue;
            return { label };
        }
        return null;
    }, [currentMessage, messages]);

    const total = todoList?.items.length ?? 0;
    const completed = todoList?.items.filter(
        (i) => String(i.status).toLowerCase() === 'completed',
    ).length ?? 0;

    // Resolve a single "what is the agent doing right now" label.
    // Priority (highest first):
    //   1. The currently-in-progress task title from the parsed
    //      todoList (best signal — the agent itself flagged it).
    //   2. Any in-progress item even when `currentWorkingItemKey`
    //      isn't set (older agents don't always populate it).
    //   3. The latest function-call's friendly label (e.g. "Consultando
    //      série de vendas") — this is what most agents actually emit
    //      and it's what `AgentActivityV2` shows in the sidebar; the
    //      strip needs to agree on that phrasing.
    //   4. `lastToolEvent.eventName` (humanized) — covers agent-level
    //      side events like `dashboardGenerated` that aren't function
    //      calls but still represent meaningful activity.
    //   5. Generic "em execução…" while a turn is open.
    const activityLabel = useMemo<string | null>(() => {
        if (todoList?.currentWorkingItemKey) {
            const current = todoList.items.find((i) => i.key === todoList.currentWorkingItemKey);
            if (current?.label) return current.label;
        }
        const inProgress = todoList?.items.find(
            (i) => String(i.status).toLowerCase() === 'in_progress',
        );

        const inProgressLabel = inProgress?.label ? `${inProgress?.label} - ` : '';
        let label = `${inProgressLabel}`

        if (lastToolEvent?.eventName && lastToolEvent.eventName !== 'todolistUpdated') {
            label += humanizeEventName(lastToolEvent.eventName);
        }

        label += latestCall?.label ?? '';

        if (label == '' && turnOpen)
            label = 'em execução\u2026';

        if (!turnOpen)
            label = 'Conectado'

        return label;
    }, [todoList, latestCall, lastToolEvent?.eventName, turnOpen]);

    // Connection dot color matches the full panel's mapping so users
    // who recognize the sidebar version see the same visual grammar.
    const connectionDisplay = useMemo(() => {
        switch (connectionStatus) {
            case ConnectionState.Connected:
                return {
                    color: theme.palette.success.main,
                    label: turnOpen ? 'Conexão ativa · executando' : 'Conexão ativa',
                    pulse: turnOpen,
                };
            case ConnectionState.Connecting:
                return { color: theme.palette.primary.main, label: 'Conectando\u2026', pulse: true };
            case ConnectionState.Disconnected:
                return { color: theme.palette.secondary.main, label: 'Desconectado', pulse: false };
            case ConnectionState.Idle:
            default:
                return { color: theme.palette.text.disabled, label: 'Sessão inativa', pulse: false };
        }
    }, [connectionStatus, turnOpen, theme]);

    // Hide the strip entirely when there's nothing happening — keeps
    // the host's chrome from gaining a permanent status line that
    // never says anything useful. `activityLabel` already accounts
    // for the function-call fallback, so just checking it is enough.
    const hasContent =
        turnOpen
        || total > 0
        || activityLabel !== null
        || connectionStatus === ConnectionState.Connecting
        || connectionStatus === ConnectionState.Disconnected;
    if (!hasContent) return null;

    return (
        <Box
            sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.5,
                minHeight: 28,
                backgroundColor: theme.palette.background.default,
                borderTop: `1px solid ${theme.palette.divider}`,
                flexShrink: 0,
                overflow: 'hidden',
            })}
        >
            <style>{pulseKeyframes}</style>

            {/* Connection dot */}
            <Tooltip title={connectionDisplay.label} placement="top">
                <Box
                    sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        backgroundColor: connectionDisplay.color,
                        animation: connectionDisplay.pulse ? 'v2rpspulse 1.4s ease-in-out infinite' : 'none',
                        flexShrink: 0,
                    }}
                />
            </Tooltip>

            {/* Activity label — flex: 1 so it absorbs the row's slack
                and ellipses long task titles. */}
            {activityLabel && (
                <Typography
                    noWrap
                    sx={(theme) => ({
                        flex: 1,
                        minWidth: 0,
                        fontSize: 11,
                        color: theme.palette.text.secondary,
                        fontFamily: theme.typography.fontFamilyMono,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    })}
                >
                    {activityLabel}
                </Typography>
            )}

            {/* Spacer when there's no activity label so chips still
                align to the right edge. */}
            {!activityLabel && <Box sx={{ flex: 1 }} />}

            {/* Task progress chip */}
            {total > 0 && (
                <Tooltip title={`Tarefas: ${completed}/${total}`} placement="top">
                    <Box
                        sx={(theme) => ({
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.25,
                            px: 0.75,
                            height: 20,
                            borderRadius: 0.75,
                            backgroundColor: theme.palette.surface.raised,
                            border: `1px solid ${theme.palette.divider}`,
                            color: theme.palette.text.primary,
                            fontFamily: theme.typography.fontFamilyMono,
                            fontSize: 10,
                            flexShrink: 0,
                        })}
                    >
                        <Box component="span">{completed}</Box>
                        <Box component="span" sx={(theme) => ({ color: theme.palette.text.disabled })}>
                            /{total}
                        </Box>
                    </Box>
                </Tooltip>
            )}

            {/* Tool calls counter — leaf with its own subscription */}
            <ToolCountChip />
        </Box>
    );
};

RunProgressStripV2.displayName = 'RunProgressStripV2';
